import styled from '@emotion/styled';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { createElement, FC, useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Prompt } from 'react-router';

import { ProjectDetailsForShowFragment } from '../../../../generated/graphql';
import useInputComment, { inputCommentLimit } from '../../../../hooks/useInputComment';
import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { context as StateContext } from '../../../context/state';

interface Props {
  project: ProjectDetailsForShowFragment;
  className?: string;
  passedInspection: boolean;
  onSubmitProjectIssue: (projectId: string, inputComment: string) => Promise<void>;
  onSubmitProjectIssueComment: (issueId: string, comment: string) => void;
}

const ProjectIssueForm: FC<Props> = ({
  project,
  className,
  passedInspection,
  onSubmitProjectIssue,
  onSubmitProjectIssueComment,
}) => {
  const [inputComment, commentHandler] = useInputComment('');
  const state = useContext(StateContext);

  useEffect(() => {
    const setDeleteConfirmAlert = (e: BeforeUnloadEvent) => {
      if (inputComment.body.length > 0) {
        e.preventDefault(); // for IE and Edge
        e.returnValue = 'alert';
      }
    };

    window.addEventListener('beforeunload', setDeleteConfirmAlert);
    return () => {
      window.removeEventListener('beforeunload', setDeleteConfirmAlert);
    };
  }, [inputComment]);

  return (
    <Root className={className} id={'issue.id'}>
      <Heading>
        <Title>
          <FormattedMessage id="show_project_page.project_issue.input_comment.title" />
        </Title>
        {state.language == 'ja-JP' ? (
          <FormattedMessage id="show_project_page.project_issue.input_comment.help">
            {(text: string) => (
              <Tooltip placement="top" title={text}>
                <HelpAnchor
                  href="https://help.kaizen-ad.com/ja/articles/5362169-初稿納品前コメント機能について"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Help />
                </HelpAnchor>
              </Tooltip>
            )}
          </FormattedMessage>
        ) : null}
        {state.language == 'ja-JP' ? (
          <CommentFormInfo>
            <FormattedMessage id="show_project_page.project_issue.input_comment.info" />{' '}
          </CommentFormInfo>
        ) : null}
      </Heading>
      <FormArea>
        {passedInspection ? (
          <CommentForm error={inputComment.error}>
            <DisabledComment>
              <DisabledMessage>
                <FormattedMessage id="show_project_page.project_issue.input_comment.disabled" />
              </DisabledMessage>
            </DisabledComment>
            <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
          </CommentForm>
        ) : (
          <CommentForm error={inputComment.error} variant="outlined">
            <InputLabel htmlFor="component-simple">
              <FormattedMessage id="show_project_page.project_issue.input_comment.label" />
            </InputLabel>
            <Comment
              label={<FormattedMessage id="show_project_page.project_issue.input_comment.label" />}
              value={inputComment.body}
              rows="4"
              rowsMax="8"
              multiline
              error={inputComment.error}
              onChange={(e) => commentHandler.setComment(e.target.value)}
            />
            <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
          </CommentForm>
        )}

        <Buttons>
          <ActionButton
            disabled={passedInspection || inputComment.body.trim() === '' || inputComment.error}
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              commentHandler.setComment('');
              project.issue
                ? onSubmitProjectIssueComment(project.issue.id, inputComment.body)
                : onSubmitProjectIssue(project.id, inputComment.body);
            }}
          >
            <FormattedMessage id="show_project_page.project_issue.input_comment.submit" />
          </ActionButton>
        </Buttons>
      </FormArea>
      <FormattedMessage id="show_project_page.confirm_alert.text">
        {(text: string) => <Prompt when={inputComment.body.length > 0} message={text} />}
      </FormattedMessage>
    </Root>
  );
};

const Root = styled('div')({});

const Heading = styled('div')({
  marginBottom: SPACING_REGULAR,
  display: 'flex',
});

const Title = styled(Typography)({});
Title.defaultProps = { variant: 'h6', color: 'textSecondary' };

const CommentFormInfo = styled(Typography)({
  marginLeft: SPACING_UNIT,
  display: 'inline-flex',
  alignItems: 'center',
});

const HelpAnchor = styled('a')({
  marginLeft: SPACING_UNIT,
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.grey['500'],
  textDecoration: 'none',
});

const FormArea = styled('div')({
  width: '100%',
});

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_UNIT,
});

const Comment = styled(OutlinedInput)({
  width: '100%',
});

const InputCount = styled(FormHelperText)({
  textAlign: 'right',
});

const Buttons = styled('div')({
  textAlign: 'right',
});
const ActionButton = styled(Button)({
  width: 100,
  marginLeft: 10,
});

const DisabledComment = styled(Box)({
  borderRadius: 4,
  border: '1px solid rgba(0, 0, 0, 0.12)',
  height: '113px',
  width: '100%',
  background: `${theme.palette.grey['400']}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
});
const DisabledMessage = styled(Typography)({
  fontSize: '18px',
});

export default ProjectIssueForm;
